"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { useAnimate } from "framer-motion";
interface DecisionPoint {
  id: string;
  date: string;
  winnersCount?: number | undefined; // Optional count of winners
}
interface WaveWinnersSmallDecisionSelectorProps {
  readonly decisionPoints: DecisionPoint[];
  readonly activeDecisionPoint: string | null;
  readonly onChange: (decisionPointId: string) => void;
}
export const WaveWinnersSmallDecisionSelector: React.FC<WaveWinnersSmallDecisionSelectorProps> = t0 => {
  const $ = _c(28);
  const {
    decisionPoints,
    activeDecisionPoint,
    onChange
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [iconScope, animateIcon] = useAnimate();
  const dropdownRef = useRef(null);
  let t1;
  if ($[0] !== animateIcon || $[1] !== iconScope || $[2] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        animateIcon(iconScope.current, {
          rotate: 0
        });
      } else {
        animateIcon(iconScope.current, {
          rotate: -90
        });
      }
    };
    $[0] = animateIcon;
    $[1] = iconScope;
    $[2] = isOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isOpen) {
    t2 = [isOpen];
    $[4] = isOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    };
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== activeDecisionPoint || $[9] !== decisionPoints) {
    t5 = () => {
      const currentPoint = decisionPoints.find(point => point.id === activeDecisionPoint);
      if (!currentPoint) {
        return "Select decision point";
      }
      return `${format(new Date(currentPoint.date), "MMM d, yyyy h:mm a")}${currentPoint.winnersCount !== undefined ? ` (${currentPoint.winnersCount} winner${currentPoint.winnersCount !== 1 ? "s" : ""})` : ""}`;
    };
    $[8] = activeDecisionPoint;
    $[9] = decisionPoints;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getCurrentLabel = t5;
  let t6;
  if ($[11] !== isOpen) {
    t6 = () => setIsOpen(!isOpen);
    $[11] = isOpen;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = getCurrentLabel();
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== iconScope) {
    t9 = <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center -tw-mr-1 tw-pr-3.5"><svg ref={iconScope} className="tw-h-5 tw-w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">{t8}</svg></div>;
    $[14] = iconScope;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t9) {
    t10 = <button type="button" aria-haspopup="true" onClick={t6} className="tw-w-full tw-text-sm tw-text-left tw-relative tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-py-2.5 tw-pl-3.5 tw-pr-10 tw-font-medium tw-text-iron-300 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 hover:tw-bg-iron-800/80 hover:tw-ring-iron-600 tw-transition tw-duration-300 tw-ease-out">{t7}{t9}</button>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== activeDecisionPoint || $[21] !== decisionPoints || $[22] !== isOpen || $[23] !== onChange) {
    t11 = isOpen && <div className="tw-absolute tw-z-10 tw-mt-1 tw-w-full tw-rounded-md tw-bg-iron-800 tw-shadow-lg tw-ring-1 tw-ring-iron-700 tw-max-h-60 tw-overflow-auto"><ul className="tw-py-1 tw-list-none tw-pl-0 tw-mb-0 tw-text-sm tw-text-iron-300">{decisionPoints.map(point_0 => <li key={point_0.id} onClick={() => {
          onChange(point_0.id);
          setIsOpen(false);
        }} className={`tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-iron-700/50 tw-transition-colors tw-duration-150 tw-ease-out ${point_0.id === activeDecisionPoint ? "tw-bg-iron-700 tw-text-primary-400" : ""}`}>{format(new Date(point_0.date), "MMM d, yyyy h:mm a")}{point_0.winnersCount !== undefined && <span className="tw-ml-1 tw-text-iron-400">({point_0.winnersCount} winner{point_0.winnersCount !== 1 ? "s" : ""})</span>}</li>)}</ul></div>;
    $[20] = activeDecisionPoint;
    $[21] = decisionPoints;
    $[22] = isOpen;
    $[23] = onChange;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="tw-mt-2 tw-mb-3 tw-relative" ref={dropdownRef}>{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};